import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Select, { components } from 'react-select';
import CountUp from 'react-countup';
import {
    RotateCcw, Ship, Activity, ChartScatter, Microscope, CloudSun, FlaskConical, Waves, Sprout, Skull,
    Search, AudioLines, Download, Compass, Home, Calendar, MapPin, FishIcon, Info
} from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import axios from 'axios';

import Footer from './footer.jsx';
import Disclaimer from '../../../components/Disclaimer.jsx';
import TourGuiaPublico from '../../../components/TourGuiaPublico.jsx';
import TourAcuicultura from '../../../components/TourAcuicultura.jsx';

import LoginModal from '../../../components/Auth/LoginModal.jsx';
import RegisterModal from '../../../components/Auth/RegisterModal.jsx';
import ForgotPasswordModal from '../../../components/Auth/ForgotPasswordModal.jsx';

import { useAuth } from '../../../contexts/AuthContext.jsx';

import './estiloAcuicultura.css';

const ICON_SIZE_DEFAULT = 20;
const strokeWidth = 1.5;

const ICON_CONFIG = {
    'Abundancia': <AudioLines size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Bitácora de crucero': <Ship size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Bitácora': <Ship size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Fitoplancton cualitativo': <ChartScatter size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Fitoplancton cuantitativo': <Microscope size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Meteorológica': <CloudSun size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Nutrientes': <FlaskConical size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Oceanografia': <Waves size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Quistes': <Sprout size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
    'Toxinas': <Skull size={ICON_SIZE_DEFAULT} strokeWidth={strokeWidth} className="text-info__ text-marine-800 fw-normal" />,
};

const ICON_CONFIG_TABLA = {
    'Abundancia': <AudioLines strokeWidth={strokeWidth} className="" />,
    'Bitácora de crucero': <Ship strokeWidth={strokeWidth} className="" />,
    'Bitácora': <Ship strokeWidth={strokeWidth} className="" />,
    'Fitoplancton Cualitativo': <ChartScatter strokeWidth={strokeWidth} className="" />,
    'Fitoplancton Cuantitativo': <Microscope strokeWidth={strokeWidth} className="" />,
    'Meteorológica': <CloudSun strokeWidth={strokeWidth} className="" />,
    'Nutrientes': <FlaskConical strokeWidth={strokeWidth} className="" />,
    'Oceanografia': <Waves strokeWidth={strokeWidth} className="" />,
    'Quistes': <Sprout strokeWidth={strokeWidth} className="" />,
    'Toxinas': <Skull strokeWidth={strokeWidth} className="" />,
};

const customStyles = {
    control: (styles) => ({
        ...styles,
        borderRadius: '0.75rem',
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: '14px',
        backgroundColor: state.isSelected
            ? '#f0f0f0'
            : state.isFocused
                ? '#e8e8e8'
                : 'transparent',
        color: '#333333',
        cursor: 'pointer',
        '&:active': { backgroundColor: '#dcdcdc' },
        opacity: state.isDisabled ? 0.5 : 1,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
    menu: (provided) => ({
        ...provided,
        minWidth: '100%',
        width: 'max-content',
        left: 0,
    }),
    menuPortal: base => ({ ...base, zIndex: 10000005 }),
};

const isInvalidStyles = {
    control: (provided, state) => ({
        ...customStyles.control(provided, state),
        borderColor: '#dc3545',
        outline: 0,

        // Icono SVG
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath d='M6 8h.01M6 3v3.5' stroke-width='1.5' stroke-linecap='round'/%3e%3c/svg%3e")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 3rem center',
        backgroundSize: '16px 16px',
        minHeight: '38px',

        // 1. SOLUCIÓN DEFINITIVA: Desactivamos las animaciones/transiciones nativas
        // Esto evita que el fondo intente animarse cuando cambia el contenido interno
        transition: 'none',

        '&:hover': {
            borderColor: '#dc3545',
            transition: 'none', // Aseguramos que al pasar el mouse tampoco haga transiciones raras
        },

        // El halo de Bootstrap (este sí puede tener una pequeña transición si lo deseas,
        // pero al poner transition 'none' arriba, aparecerá de golpe, lo cual es más limpio aquí)
        boxShadow: state.isFocused
            ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
            : provided.boxShadow,
    }),
}

const getBadgeClass = (group) => {
    const g = group.toLowerCase().trim();
    if (g.includes('abundancia')) return 'badge-abundancia';
    if (g.includes('bitácora') || g.includes('bitacora')) return 'badge-bitacora';
    if (g.includes('fitoplancton cualitativo')) return 'badge-cualitativo';
    if (g.includes('fitoplancton cuantitativo')) return 'badge-cuantitativo';
    if (g.includes('nutrientes')) return 'badge-nutrientes';
    if (g.includes('meteorológica') || g.includes('meteorologica')) return 'badge-meteorologia';
    if (g.includes('oceanografía') || g.includes('oceanografia')) return 'badge-oceanografia';
    if (g.includes('quistes')) return 'badge-quistes';
    if (g.includes('toxinas')) return 'badge-toxinas';
    return '';
};

const AcuiculturaNuevoV1Page = ({ verifyToken }) =>
{
    const [fetch, setFetch] = useState(false);
    const [selectores, setSelectores] = useState(false);
    const [buscar, setBuscar] = useState(false);
    const [filter, setFilter] = useState(false);
    const [regiones, setRegiones] = useState(false);
    const [checkboxes, setCheckboxes] = useState(false);
    const [descargar, setDescargar] = useState(false);
    const [estadisticas, setEstadisticas] = useState([]);
    const [proyectos, setProyectos] = useState([]);
    const [proximamente, setProximamente] = useState([]);
    const [selectorTiposInformacion, setSelectorTiposInformacion] = useState([]);
    const [valueTiposInformacion, setValueTipoInformacion] = useState([]);
    const [anioDisponible, setAnioDisponible] = useState(0);
    const [selectorAnios, setSelectorAnios] = useState([]);
    const [valueAnio, setValueAnio] = useState(null);
    const [codigoRegion, setCodigoRegion] = useState(0);
    const [selectorRegiones, setSelectorRegiones] = useState([]);
    const [valueRegion, setValueRegion] = useState(null);
    const [archivos, setArchivos] = useState([]);
    const [tiposInformacion, setTiposInformacion] = useState([]);
    const [checkboxesTiposInformacion, setCheckboxesTiposInformacion] = useState({});
    const [descargas, setDescargas] = useState([]);
    const [descargasConsolidadas, setDescargasConsolidadas] = useState([]);
    const [isMobile, setIsMobile] = useState(false);
    const [params, setParams] = useState({});
    const [paramsConsolidados, setParamsConsolidados] = useState({});
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [enabledTour, setEnabledTour] = useState(false);

    const { api, setEmail, setPassword, token } = useAuth();

    const publicUrl = process.env.PUBLIC_URL;
    const appDebug = process.env.REACT_APP_DEBUG;
    const apiUrl = process.env.REACT_APP_API_URL;
    const repositorioUrl = process.env.REACT_APP_REPOSITORIO_URL;
    const secretKey = process.env.REACT_APP_SECRET_KEY;

    const url = 'buscador-publico-acuicultura';

    const location = useLocation();

    const CustomOption = (props) =>
    {
        return (
            <components.Option {...props}>
                <div style={{ display: 'flex', alignContent: 'center', gap: '8px' }}>
                    <input
                        type="checkbox"
                        className="form-check-input border-dark-subtle"
                        checked={props.isSelected}
                        onChange={() => null}
                    />
                    <label>{props.label}</label>
                </div>
            </components.Option>
        );
    };

    const CustomMenuList = (props) =>
    {
        return (
            <components.MenuList {...props}>
                <div
                    style={
                    {
                        display: 'grid',
                        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                        gap: '10px',
                        padding: '10px',
                    }}
                >
                    {props.children}
                </div>
            </components.MenuList>
        );
    };

    const handleSelectorTiposInformacion = (tiposInformacion, action) =>
    {
        const option = action.option;

        let tiposInformacionArray = [];

        // 1. Tu lógica actual de React (actualizar estado)
        if (option)
        {
            if (option.value === '0')
            {
                if (valueTiposInformacion.find(item => item.value === '0'))
                {
                    tiposInformacionArray = [];

                    setValueTipoInformacion([]);
                }

                if (!valueTiposInformacion.find(item => item.value === '0'))
                {
                    tiposInformacionArray.push(option);

                    setValueTipoInformacion([option]);
                }
            }
            else
            {
                const filter = tiposInformacion.filter(item => item.value !== '0');

                tiposInformacionArray = filter;

                setValueTipoInformacion(filter);
            }
        }
        else
        {
            setValueTipoInformacion(tiposInformacion);
        }

        // 2. Notificar a Google Tag Manager
        if (window.dataLayer && tiposInformacion.length > 0)
        {
            window.dataLayer.push(
            {
                event: 'buscador_publico_acuicultura_filtro_por_puertos',
                tipos_informacion_seleccionados: tiposInformacionArray.map(tipoInformacion => tipoInformacion.label),
            });
        }

        setSelectores(true);

        setAnioDisponible(0);
        setValueAnio(null);
        setCodigoRegion(0);
        setValueRegion(null);
        setArchivos([]);
        setTiposInformacion([]);
        setCheckboxesTiposInformacion({});
        setDescargas([]);
        setDescargasConsolidadas([]);
    };

    const handleSelectorAnio = (anio) =>
    {
        // 1. Tu lógica actual de React (actualizar estado)
        setAnioDisponible(anio ? anio.value : 0);
        setValueAnio(anio);

        // 2. Notificar a Google Tag Manager
        if (window.dataLayer && anio)
        {
            window.dataLayer.push(
            {
                event: 'buscador_publico_acuicultura_filtro_por_anio',
                anio_seleccionado: anio.label,
            });
        }

        setRegiones(true);

        setCodigoRegion(0);
        setValueRegion(null);
        setArchivos([]);
        setTiposInformacion([]);
        setCheckboxesTiposInformacion({});
        setDescargas([]);
        setDescargasConsolidadas([]);
    };

    const handleSelectorRegion = (region) =>
    {
        // 1. Tu lógica actual de React (actualizar estado)
        setCodigoRegion(region ? region.value : 0);
        setValueRegion(region);

        // 2. Notificar a Google Tag Manager
        if (window.dataLayer && region)
        {
            window.dataLayer.push(
            {
                event: 'buscador_publico_acuicultura_filtro_por_region',
                region_seleccionada: region.label,
                region_id: region.value,
            });
        }

        setArchivos([]);
        setTiposInformacion([]);
        setCheckboxesTiposInformacion({});
        setDescargas([]);
        setDescargasConsolidadas([]);
    };

    const handleIsOptionDisabled = (option) =>
    {
        const seleccionado = valueTiposInformacion.find(tipoInformacion => tipoInformacion.value === '0');

        if (seleccionado) { return option.value !== '0'; }

        // if (valueTiposInformacion.length > 0 && option.value === '0') { return true; }

        return false;
    }

    const handleBuscar = () =>
    {
        // 1. Enviar los filtros activos a Google Tag Manager
        if (window.dataLayer)
        {
            window.dataLayer.push(
            {
                event: 'busqueda_publica_acuicultura_iniciada',
                filtro_tipos_informacion: valueTiposInformacion ? valueTiposInformacion.map(tipoInformacion => tipoInformacion?.label || '') : [],
                filtro_anio: anioDisponible,
                filtro_region: valueRegion ? valueRegion.label : '',
            });
        }

        // 2. Tu lógica actual de React para hacer la petición a la API
        setBuscar(true);

        setTiposInformacion([]);
        setCheckboxesTiposInformacion({});
    };

    const handleReset = () =>
    {
        // 1. Enviar los filtros activos a Google Tag Manager
        if (window.dataLayer)
        {
            window.dataLayer.push(
            {
                event: 'busqueda_publica_acuicultura_reiniciada',
                filtro_tipos_informacion: valueTiposInformacion ? valueTiposInformacion.map(tipoInformacion => tipoInformacion?.label || '') : [],
                filtro_anio: anioDisponible,
                filtro_region: valueRegion ? valueRegion.label : '',
            });
        }

        setFilter(false);
        setValueTipoInformacion([]);
        setAnioDisponible(0);
        setSelectorAnios([]);
        setValueAnio(null);
        setCodigoRegion(0);
        setSelectorRegiones([]);
        setValueRegion(null)
        setArchivos([]);
        setTiposInformacion([]);
        setCheckboxesTiposInformacion({});
        setDescargas([]);
        setDescargasConsolidadas([]);
    };

    const handleCheckbox = useCallback((e, value) =>
    {
        const checked = e.target.checked;
        const tipoInformacion = value;

        // let tiposInformacionArray = [];

        if (checked === true)
        {
            const array = [...tiposInformacion];

            array.push(tipoInformacion);

            // tiposInformacionArray = array

            setTiposInformacion(array);
        }
        else
        {
            const filter = tiposInformacion.filter(nombre => nombre !== tipoInformacion);

            // tiposInformacionArray = filter

            setTiposInformacion(filter);
        }

        // 1. Tu lógica para actualizar el estado en React
        setCheckboxesTiposInformacion(
        {
            ...checkboxesTiposInformacion,
            [tipoInformacion]: checked
        });

        // 2. Enviar la acción a Google Tag Manager
        if (window.dataLayer)
        {
            window.dataLayer.push(
            {
                event: 'buscador_publico_acuicultura_checkbox_tipo_informacion',
                checkbox_tipo_informacion: tipoInformacion,
                checkbox_accion: checked ? 'marcado' : 'desmarcado',
            });
        }

        setCheckboxes(true);
    }, [tiposInformacion, checkboxesTiposInformacion]);

    const handleOpciones = (href, archivo, url, tipoInformacion, proyecto, valueRegion, anioDisponible) =>
    {
        setParams(
        {
            href,
            archivo,
            url,
            tipo_informacion: tipoInformacion,
            proyecto,
            region: valueRegion,
            anio: anioDisponible,
        });
    }

    const handleOpcionesConsolidadas = (descargasConsolidadas, url, tiposInformacion, valueTiposInformacion, valueRegion, anioDisponible) =>
    {
        setParamsConsolidados(
        {
            archivos: descargasConsolidadas,
            url,
            proyectos: tiposInformacion,
            tipos_informacion: valueTiposInformacion,
            region: valueRegion,
            anio: anioDisponible,
        });
    }

    const handleClick = async (archivo, url, tipoInformacion, proyecto, valueRegion, anioDisponible) =>
    {
        if (Object.keys(params).length > 0)
        {
            const link = document.createElement('a');

            link.href = params.href;

            link.setAttribute('download', archivo);

            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);

            setParams({});
        }

        const region = valueRegion?.label || '';

        const opciones = `Tipo de Información: ${tipoInformacion}, Proyecto: ${proyecto}, Región: ${region}`;

        try
        {
            const response = await api.post(`/v2/acuicultura/archivos/descargas/${archivo}`,
            {
                url: url,
                opciones: opciones,
                años: anioDisponible
            });

            // Enviamos todo el contexto científico a GTM
            if (window.dataLayer)
            {
                window.dataLayer.push(
                {
                    event: 'buscador_publico_acuicultura_descarga_archivo',
                    archivo_nombre: archivo,
                    archivo_tipo_informacion: tipoInformacion,
                    archivo_proyecto: proyecto,
                    filtro_tipos_informacion: valueTiposInformacion.map(tipoInformacion => tipoInformacion?.label || ''),
                    filtro_anio: anioDisponible,
                    filtro_region: valueRegion?.label || '',
                });
            }

            console.log(response);
        }
        catch (error)
        {
            console.error(error);
        }
    }

    const handleDownloadZip = async (descargasConsolidadas, url, tiposInformacion, valueTiposInformacion, valueRegion, anioDisponible) =>
    {
        if (Object.keys(paramsConsolidados).length > 0)
        {
            setDescargar(true);

            setParamsConsolidados({});
        }

        const region = valueRegion.label;

        let archivos = [];
        let opcionesCheckbox = '';

        descargasConsolidadas.forEach((archivo) =>
        {
            archivos.push(`${archivo}.xlsx`);
        });

        valueTiposInformacion.forEach(data =>
        {
            opcionesCheckbox += `Tipo de Información: ${data.label}, `;
        });

        opcionesCheckbox += `Región: ${region}`;

        try
        {
            const zip = new JSZip();

            const descargasPromise = descargasConsolidadas.map(async (nombre) =>
            {
                const response = await api.get(`/v2/acuicultura/archivos/descargas/${nombre}/xlsx`, { responseType: 'blob' });

                zip.file(`${nombre}.xlsx`, response.data);
            });

            await Promise.all(descargasPromise);

            const content = await zip.generateAsync({ type: 'blob' });
            const timestamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);

            const archivo = `descargas_consolidadas_${timestamp}.zip`;

            saveAs(content, archivo);

            const response = api.post(`/v2/acuicultura/archivos/descargas/${archivo}`,
            {
                archivos: archivos,
                url: url,
                opciones: opcionesCheckbox,
                años: anioDisponible
            });

            // Enviamos todo el contexto científico a GTM
            if (window.dataLayer)
            {
                window.dataLayer.push(
                {
                    event: 'buscador_publico_acuicultura_descarga_archivo_consolidado',
                    archivo_consolidado_nombre: archivo,
                    archivo_consolidado_tipos_informacion: tiposInformacion,
                    filtro_tipos_informacion: valueTiposInformacion.map(tipoInformacion => tipoInformacion.label),
                    filtro_anio: anioDisponible,
                    filtro_region: valueRegion.label,

                });
            }

            setDescargar(false);
        }
        catch (error)
        {
            console.error(error);
        }
    };

    const filterRegiones = useCallback(async () =>
    {
        try
        {
            let tipos = '';

            valueTiposInformacion.forEach(data =>
            {
                tipos += '&tipos_informacion[]=' + data.value;
            });
    
            const response  = await axios.get(`${apiUrl}/v2/acuicultura/busqueda?${tipos}&anio_disponible=${anioDisponible}&environment=production`);

            const data = verifyToken(response, appDebug, secretKey);

            if (data.length === 0) { return console.log(new Error('Signature is not valid.')); }

            setSelectorRegiones(data.regiones.map(region => ({ value: `${region.COD_REGION}`, label: `${region.NOMBRE_REG}` })));

            setRegiones(false);
        }
        catch (error)
        {
            console.error(error);
        }
    }, [appDebug, apiUrl, secretKey, verifyToken, valueTiposInformacion, anioDisponible]);

    const filterArchivos = useCallback(async () =>
    {
        setFilter(true);

        try
        {
            let tipos = '';

            valueTiposInformacion.forEach(data =>
            {
                tipos += '&tipos_informacion[]=' + data.value;
            });

            const response = await axios.get(`${apiUrl}/v2/acuicultura/archivos?${tipos}&anios_disponibles[]=${anioDisponible}&codigos_regiones[]=${codigoRegion}`);

            const archivos = verifyToken(response, appDebug, secretKey);

            if (archivos.length === 0) { return console.log(new Error('Signature is not valid.')); }

            setArchivos(archivos.data || []);
            setDescargas([]);

            setBuscar(false);
        }
        catch (error)
        {
            console.error(error);
        }
    }, [appDebug, apiUrl, secretKey, verifyToken, valueTiposInformacion, anioDisponible, codigoRegion]);

    const filterDescargas = useCallback(async () =>
    {
        try
        {
            let tipos = '';

            tiposInformacion.forEach(data =>
            {
                tipos += '&tipos_informacion[]=' + data;
            });

            const response = await axios.get(`${apiUrl}/v2/acuicultura/archivos/descargas?${tipos}&anios_disponibles[]=${anioDisponible}&codigos_regiones[]=${codigoRegion}`);

            const descargas = verifyToken(response, appDebug, secretKey);

            if (descargas.length === 0) { return console.log(new Error('Signature is not valid.')); }

            setDescargas(descargas.data || []);

            setDescargasConsolidadas(descargas.data.map(e =>
            (
                e.NOMBRE_ARCHIVO.replace(/\.csv$/i, '')
            )));

            setCheckboxes(false);
        }
        catch (error)
        {
            console.error(error);
        }
    }, [appDebug, apiUrl, secretKey, verifyToken, tiposInformacion, anioDisponible, codigoRegion]);

    const fetchData = useCallback(async () =>
    {
        setFetch(true);

        try
        {
            const responseEstadisticas = await axios.get(`${apiUrl}/v2/acuicultura/estadisticas?environment=production`);

            const estadisticas = verifyToken(responseEstadisticas, appDebug, secretKey);

            if (estadisticas.length === 0) { return console.log(new Error('Signature is not valid.')); }

            setEstadisticas(estadisticas.data || []);
            setProyectos(estadisticas.proyectos || []);
            setProximamente(estadisticas.proximamente || []);

            const responseBusqueda = await axios.get(`${apiUrl}/v2/acuicultura/busqueda?environment=production`);

            const busqueda = verifyToken(responseBusqueda, appDebug, secretKey);

            if (busqueda.length === 0) { return console.log(new Error('Signature is not valid.')); }

            setSelectorTiposInformacion(busqueda.tipos_informacion.map(e =>
            ({
                value: `${e.TIPO_INFORMACION}`,
                label: `${e.TIPO_INFORMACION}`,
            })));
        }
        catch (error)
        {
            console.error(error);
        }
    }, [appDebug, apiUrl, secretKey, verifyToken]);

    const fetchSelectores = useCallback(async () =>
    {
        try
        {
            let tipos = '';

            valueTiposInformacion.forEach(data =>
            {
                tipos += '&tipos_informacion[]=' + data.value;
            });
    
            const response  = await axios.get(`${apiUrl}/v2/acuicultura/busqueda?${tipos}&environment=production`);

            const data = verifyToken(response , appDebug, secretKey);

            if (data.length === 0) { return console.log(new Error('Signature is not valid.')); }

            setSelectorAnios(data.anios.map(anio => ({ value: `${anio.ANIO}`, label: `${anio.ANIO}` })));

            setSelectorRegiones(data.regiones.map(region => ({ value: `${region.COD_REGION}`, label: `${region.NOMBRE_REG}` })));

            setSelectores(false);
        }
        catch (error)
        {
            console.error(error);
        }
    }, [appDebug, apiUrl, secretKey, verifyToken, valueTiposInformacion]);

    useEffect(() =>
    {
        const tituloAnterior = document.title;

        document.title = "Datos Acuícolas";

        return () => { document.title = tituloAnterior; };
    }, []);

    useEffect(() =>
    {
        fetch === false && fetchData();
    }, [fetchData, fetch]);

    useEffect(() =>
    {
        selectores === true && fetchSelectores();
    }, [fetchSelectores, selectores]);

    useEffect(() =>
    {
        regiones === true && filterRegiones();
    }, [filterRegiones, regiones]);

    useEffect(() =>
    {
        buscar === true && filterArchivos();
    }, [filterArchivos, buscar]);

    useEffect(() =>
    {
        checkboxes === true && tiposInformacion.length === 0 && setDescargas([]);

        checkboxes === true && tiposInformacion.length > 0 && filterDescargas();
    }, [filterDescargas, checkboxes, tiposInformacion]);

    useEffect(() =>
    {
        const handleResize = () =>
        {
            setIsMobile(window.innerWidth < 600);
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() =>
    {
        ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }, [location]);

    // Tutorial Effects
    const handleCloseTour = () =>
    {
        setEnabledTour(false);
    };

    const handleStartTour = () =>
    {
        handleReset(); // Limpiar todo (filtros y resultados) para iniciar desde cero
        setEnabledTour(true);
    };

    const tourSteps = [
        {
          element: '#tour-pub-tipo-informacion',
          intro: '<b style="color: #003366; font-size: 1.1rem;">1. Selecciona la Información</b><br/><br/>Elige el tipo de información o grupos de tipos de información que deseas consultar.<br/><br/><i class="tour-validation-msg" style="color: #ff0000; font-size: 0.85rem; display: none;">* Se requiere agregar la selección para continuar.</i>',
          position: 'right',
          disableInteraction: false,
        },
        {
          element: '#tour-pub-anio',
          intro: '<b style="color: #003366; font-size: 1.1rem;">2. Elige el Período</b><br/><br/>Selecciona el año de interés para tu búsqueda.<br/><br/><i class="tour-validation-msg" style="color: #ff0000; font-size: 0.85rem; display: none;">* Se requiere agregar la selección para continuar.</i>',
          position: 'right',
          disableInteraction: false,
        },
        {
          element: '#tour-pub-region',
          intro: '<b style="color: #003366; font-size: 1.1rem;">3. Filtra por Región:</b><br/><br/>Indica la zona geográfica de los datos.<br/><br/><i class="tour-validation-msg" style="color: #ff0000; font-size: 0.85rem; display: none;">* Se requiere agregar la selección para continuar.</i>',
          position: 'left',
          disableInteraction: false,
        },
        {
          element: '#tour-pub-buscar',
          intro: '<b style="color: #d9534f; font-size: 1.1rem;">4. ¡Busca los Datos!</b><br/><br/>Haz clic aquí para procesar tus filtros y ver los resultados.<br/><br/><i class="tour-validation-msg" style="color: #ff0000; font-size: 0.85rem; display: none;">* Por favor, completa los filtros obligatorios para continuar.</i>',
          position: 'left',
          disableInteraction: false,
        },
        {
          element: '#tour-pub-resultados-container',
          intro: '<b style="color: #003366; font-size: 1.1rem;">5. Explora y Descarga</b><br/><br/>Aquí aparecerán los resultados consolidados. Selecciona los tipos de información para habilitar la descarga y verás cómo se despliega la tabla inferior.',
          position: 'right',
          tooltipClass: 'custom-tooltip-style tooltip-offset-right',
          disableInteraction: false,
        },
        {
          element: '#tour-pub-resultados-container',
          intro: '<b style="color: #003366; font-size: 1.1rem;">6. Descarga Final</b><br/><br/>¡Aquí tienes tus archivos! Puedes descargar registros individuales en <b>XLSX</b> o un <b>ZIP consolidado</b> con toda tu selección.',
          position: 'right',
          tooltipClass: 'custom-tooltip-style tooltip-offset-right',
          disableInteraction: false,
        }
    ];

    const tourValidators = {
        'tour-pub-especie': () => document.getElementById('tour-pub-especie')?.querySelectorAll('.select__single-value').length > 0,
        'tour-pub-anio': () => document.getElementById('tour-pub-anio')?.querySelectorAll('.select__single-value').length > 0,
        'tour-pub-puerto': () => document.getElementById('tour-pub-puerto')?.querySelectorAll('.select__single-value').length > 0,
        'tour-pub-buscar': () => document.getElementById('tour-pub-resultados-container')?.querySelector('#tour-pub-primer-card') !== null,
    };

    return (
        <>
            <TourGuiaPublico
                enabled={enabledTour}
                onExit={handleCloseTour}
                onAutoFillEspecie={() =>
                {
                    if (!valueTiposInformacion && selectorTiposInformacion.length > 0) { handleSelectorTiposInformacion(selectorTiposInformacion[0]); }
                }}
                onAutoFillAnio={() =>
                {
                    if (!valueAnio && selectorAnios.length > 0) { handleSelectorAnio(selectorAnios[0]); }
                }}
                onAutoFillRegion={() =>
                {
                    if (!valueRegion && selectorRegiones.length > 0) { handleSelectorRegion(selectorRegiones[0]); }
                }}
                setEspecie={setValueTipoInformacion}
                setAnio={setValueAnio}
                setRegion={setValueRegion}
            />

            <LoginModal
                show={showLoginModal}
                registerModal={() => setShowRegisterModal(true)}
                forgotPasswordModal={() => setShowForgotPasswordModal(true)}
                onClose={() =>
                {
                    setEmail('');
                    setPassword('');
                    setParams({});
                    setParamsConsolidados({});
                    setShowLoginModal(false);
                }}
                onLoginSuccess={() =>
                {
                    setShowLoginModal(false);

                    Object.keys(params).length > 0 && handleClick(params.archivo, params.url, params.tipo_informacion, params.proyecto, params.region, params.anio);
                    Object.keys(paramsConsolidados).length > 0 && handleDownloadZip(paramsConsolidados.archivos, paramsConsolidados.url, paramsConsolidados.proyectos, paramsConsolidados.tipos_informacion, paramsConsolidados.region, paramsConsolidados.anio);
                }}
            />

            <RegisterModal
                show={showRegisterModal}
                loginModal={() => setShowLoginModal(true)}
                onClose={() => setShowRegisterModal(false)}
                onLoginSuccess={() => setShowRegisterModal(false)}
            />

            <ForgotPasswordModal
                show={showForgotPasswordModal}
                loginModal={() => setShowLoginModal(true)}
                onClose={() => setShowForgotPasswordModal(false)}
            />

            <div id="acuicultura-v1-root" className="min-vh-100 d-flex flex-column">
                <div className="container-fluid p-0 flex-grow-1 fondo-gris" >
                    <header className="video-header">
                        <img className="video-background" src="https://www.ifop.cl/wp-content/uploads/2020/07/Habitat-emergente-bajo-cultivo-de-bivalvos.jpg" alt="Fondo Acuicultura" />
                        {/* <video autoPlay muted loop className="video-background">
                            <source src="https://issesr7.ifop.cl/wp-content/uploads/2024/04/ifop_back_v4.mp4" type="video/mp4" />
                        </video> */}
                        <div className="video-overlay"></div>
                        <div className="container-fluid px-5 header-content">
                            <div className="row row-cols-1 g-1 mb-2 pt-5">
                                <div className="position-relative pt-5 z-2 h-100 container-xl px-0 d-flex flex-column justify-content-center">
                                    <div className="d-flex justify-content-between align-items-start mb-4">
                                        <div className="d-flex align-items-center gap-4">
                                            <a className="align-self-start" href={`${publicUrl}/acuicultura`}><img src={'https://datos.ifop.cl/buscador/blanco.png'} alt="Logo IFOP" style={{ width: '100px' }} /></a>
                                            <div>
                                                <h1 className="display-6 fw-bold text-white mb-2 tracking-tight">BUSCADOR DE DATOS <span className="text-marine-300">ACUÍCOLAS</span></h1>
                                                <p className="text-marine-50 fs-5 fw-light opacity-90 mb-0">Encuentra lo que necesitas para tu investigación</p>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button onClick={handleStartTour} title="Iniciar Tutorial" className="btn d-flex align-items-center gap-2 px-4 py-2 text-white rounded-3 border border-white border-opacity-10 shadow-sm" style={{ backgroundColor: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
                                                <Compass className="lucide-icon" size={16} />
                                                <span className="small fw-semibold">Visita Guiada</span>
                                            </button>
                                            <a href="https://datos.ifop.cl/" className="btn d-flex align-items-center gap-2 px-4 py-2 text-white rounded-3 border border-white border-opacity-10 shadow-sm d-none" style={{ backgroundColor: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
                                                <Home className="lucide-icon" size={16} />
                                                <span className="small fw-semibold">Volver</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>
                    <main className="flex-grow-1 container-fluid px-5 pb-5 margin_main">
                        <div className="row g-1 my-1 mb-4">
                            <div className="col">
                                <div className="card custom-card h-100 bg-white rounded-4 shadow-lg border border-light p-3">
                                    <div className="card-body p-0">
                                        <h6 className="text-uppercase mb-3"><Search size={16} className="me-2 lucide lucide-search text-marine-600" />Filtros de exploración</h6>
                                        <div className="row g-2">
                                            <div className="col-xl-6 col-lg-8 col-md-8 col-12" id="tour-pub-tipo-informacion">
                                                <label className="d-block text-uppercase fw-bold mb-2 font-size-10" style={{ letterSpacing: '0.05em' }}>Tipo de Información </label>

                                                <Select
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    hideSelectedOptions={false}
                                                    className="basic-single input-filtros"
                                                    classNamePrefix="select"
                                                    styles={selectorTiposInformacion.length > 0 && valueTiposInformacion.length === 0 ? { ...customStyles, ...isInvalidStyles } : customStyles}
                                                    isClearable
                                                    name="tipo_informacion"
                                                    value={valueTiposInformacion}
                                                    onChange={handleSelectorTiposInformacion}
                                                    isOptionDisabled={handleIsOptionDisabled}
                                                    options={selectorTiposInformacion}
                                                    noOptionsMessage={() => 'Sin resultados'}
                                                    placeholder="Seleccione al menos un Tipo de Información (*)"
                                                    isDisabled={selectorTiposInformacion.length === 0}
                                                    menuPortalTarget={document.body}
                                                    components={
                                                    {
                                                        Option: CustomOption,
                                                        MenuList: CustomMenuList,
                                                    }}
                                                />

                                                {
                                                    selectorTiposInformacion.length > 0 && valueTiposInformacion.length === 0 ?
                                                    (
                                                        <div
                                                            style={
                                                            {
                                                                width: '100%',
                                                                marginTop: '.25rem',
                                                                fontSize: '.875em',
                                                                color: '#dc3545',
                                                            }}
                                                        >
                                                            {'Seleccione al menos un Tipo de Información para continuar.'}
                                                        </div>
                                                    ) : null
                                                }

                                            </div>
                                            <div className="col-xl-2 col-lg-4 col-md-4 col-12" id="tour-pub-anio">
                                                <label className="d-block text-uppercase fw-bold mb-2 font-size-10" style={{ letterSpacing: '0.05em' }}>Año</label>

                                                <Select
                                                    className="basic-single input-filtros"
                                                    classNamePrefix="select"
                                                    styles={valueTiposInformacion.length > 0 && !anioDisponible ? { ...customStyles, ...isInvalidStyles } : customStyles}
                                                    isClearable
                                                    name="anio"
                                                    value={valueAnio}
                                                    onChange={handleSelectorAnio}
                                                    options={selectorAnios}
                                                    noOptionsMessage={() => 'Sin resultados'}
                                                    placeholder="Año"
                                                    isDisabled={valueTiposInformacion.length === 0}
                                                    menuPortalTarget={document.body}
                                                />

                                                {
                                                    valueTiposInformacion.length > 0 && !anioDisponible ?
                                                    (
                                                        <div
                                                            style={
                                                            {
                                                                width: '100%',
                                                                marginTop: '.25rem',
                                                                fontSize: '.875em',
                                                                color: '#dc3545',
                                                            }}
                                                        >
                                                            {'Seleccione un año para continuar.'}
                                                        </div>
                                                    ) : null
                                                }

                                            </div>
                                            <div className="col-xl-3 col-lg-8 col-md-8 col-12" id="tour-pub-region">
                                                <label className="d-block text-uppercase fw-bold mb-2 font-size-10" style={{ letterSpacing: '0.05em' }}>Región</label>

                                                <Select
                                                    className="basic-single input-filtros"
                                                    classNamePrefix="select"
                                                    styles={valueTiposInformacion.length > 0 && anioDisponible && !valueRegion ? { ...customStyles, ...isInvalidStyles } : customStyles}
                                                    isClearable
                                                    name="anio"
                                                    value={valueRegion}
                                                    onChange={handleSelectorRegion}
                                                    options={selectorRegiones}
                                                    noOptionsMessage={() => 'Sin resultados'}
                                                    placeholder="Región"
                                                    isDisabled={!valueAnio}
                                                    menuPortalTarget={document.body}
                                                />

                                                {
                                                    valueTiposInformacion.length > 0 && anioDisponible && !valueRegion ?
                                                    (
                                                        <div
                                                            style={
                                                            {
                                                                width: '100%',
                                                                marginTop: '.25rem',
                                                                fontSize: '.875em',
                                                                color: '#dc3545',
                                                            }}
                                                        >
                                                            {'Seleccione una región para realizar su búsqueda.'}
                                                        </div>
                                                    ) : null
                                                }

                                            </div>
                                            <div className={`col-xl-1 col-lg-4 col-md-4 col-12 d-flex ${valueTiposInformacion.length === 0 || !anioDisponible || !codigoRegion ? 'align-items-center' : 'align-items-end'} gap-2`} id="tour-pub-buscar">
                                                <button type="button" className="btn btn-primary flex-grow-1 bg-marine-600 border-0 h-40px d-flex align-items-center justify-content-center gap-2 shadow-sm rounded-3 fw-medium"
                                                    style={{ height: '42px' }} onClick={handleBuscar} disabled={(valueTiposInformacion.length === 0 || !anioDisponible || !codigoRegion) || buscar}>
                                                    <Search size={18} className="me-1" />
                                                </button>
                                                <button type="button" className="btn btn-outline-light border-light-subtle text-secondary" style={{ height: '42px' }} onClick={handleReset} disabled={(valueTiposInformacion.length === 0 || !anioDisponible || !codigoRegion) || buscar}>
                                                    <RotateCcw size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {
                            filter === false &&
                            (
                                <div className="row g-1 my-1">
                                    <div className="col">
                                        <div className="card-group shadow">

                                            {
                                                estadisticas.map((estadistica, id) =>
                                                (
                                                    <div key={id} className="card bg-white">
                                                        <div className="card-body text-center">
                                                            <h2 className="fw-bold azulIfop estadisticas-numero"><CountUp separator="." end={estadistica.data} duration={3} /></h2>
                                                            <h2 className="text-muted estadisticas-texto">{estadistica.titulo}</h2>
                                                        </div>
                                                    </div>
                                                ))
                                            }

                                        </div>

                                        {
                                            proyectos.length > 0 &&
                                            (
                                                <div className="fade-in-animation my-3 shadow">
                                                    <div className="info-banner alert alert-info">
                                                        <div className="info-icon-container">
                                                            <Info size={18} className="me-2 lucide lucide-search text-marine-600" />
                                                        </div>
                                                        <div className="info-content">
                                                            <p className="info-description text-marine-700">
                                                                <strong className="text-marine-700">{'Proyectos habilitados: '}</strong>

                                                                {
                                                                    proyectos.map((proyecto, index, array) =>
                                                                    (
                                                                        <span key={index}>
                                                                            {proyecto.nombre} { index !== array.length - 1 && (', ') }
                                                                        </span>
                                                                    ))
                                                                }

                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }

                                        {
                                            proximamente.length > 0 &&
                                            (
                                                <div className="fade-in-animation my-3 shadow">
                                                    <div className="info-banner alert alert-warning py-2">
                                                        <div className="info-icon-container">
                                                            <Info size={18} className="me-2 lucide lucide-search text-marine-600" />
                                                        </div>
                                                        <div className="info-content">
                                                            <p className="info-description text-marine-700">
                                                                <strong className="text-marine-700">{'Próximamente: '}</strong>

                                                                {
                                                                    proximamente.map((proximamente, index, array) =>
                                                                    (
                                                                        <span key={index}>
                                                                            {proximamente.nombre} { index !== array.length - 1 && (', ') }
                                                                        </span>
                                                                    ))
                                                                }

                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }

                                        <div className="fade-in-animation">
                                            <div className="row g-1 mt-1">
                                                <Disclaimer />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        <div id="tour-pub-resultados-container" className="">

                            {
                                archivos.length > 0 &&
                                (
                                    <div className="fade-in-animation">
                                        <div className="d-flex align-items-center mb-2">
                                            <div className="flex-grow-1 border-bottom border-light-subtle" style={{ height: '1px' }} />
                                            <span className="text-uppercase fw-black text-light tracking-widest px-4 py-2 border border-light-subtle rounded-pill small  card-ai-gradient" style={{ fontSize: '12px' }}>
                                                <Activity className="me-2" />Resultados
                                            </span>
                                            <div className="flex-grow-1 border-bottom border-light-subtle" style={{ height: '1px' }} />
                                        </div>
                                        <div className="info-banner">
                                            <div className="info-icon-container">
                                                <Info size={18} className="me-2 lucide lucide-search text-marine-600" />
                                            </div>
                                            <div className="info-content">
                                                <p className="info-description text-marine-700">
                                                    La información se presenta por <strong className="text-marine-700">Tipos de Información</strong>, y dentro de estos por sus <strong className="text-marine-700">Proyectos Asociados</strong>.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="row g-3 my-2 row-cols-md-2 row-cols-lg-3 row-cols-xl-4">

                                            {
                                                archivos.map((archivo, index) =>
                                                (
                                                    <div key={index} id={index === 0 ? "tour-pub-primer-card" : undefined} className="">
                                                        <div className="card custom-card h-100 overflow-hidden">
                                                            <div className="card-header bg-transparent border-bottom-0 d-flex align-items-center justify-content-between p-2 pb-1">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div className="bg-white p-2 rounded-circle shadow-sm border card-bg-gradiente-verde">{ICON_CONFIG[archivo.titulo_tipo_informacion]}</div>
                                                                    <span className="fw-bold text-dark small text-uppercase">{archivo.titulo_tipo_informacion}</span>
                                                                </div>
                                                            </div>
                                                            <div className="card-body p-3 pt-1">
                                                                <div className="row">
                                                                    <div className="col-4 border-end text-center"><span className="text-primary font-size-30 fw-bold text-marine-800">{archivo.numero_registros_totales.toLocaleString('es-CL')}</span><br /><span className="font-size-12">Total de registros</span></div>
                                                                    <div className="col-8">
                                                                        <div className="ps-2 pe-2">

                                                                            {
                                                                                archivo.tipos_informacion.map((informacion, subIndex) =>
                                                                                (
                                                                                    
                                                                                    <div key={subIndex}>
                                                                                        <div className="d-flex justify-content-between align-items-center mb-2 small">
                                                                                            <span className='col-1'>
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    className="form-check-input border-dark-subtle"
                                                                                                    id={`${archivo.titulo_tipo_informacion} (${informacion.acronimo})`}
                                                                                                    value={`${archivo.titulo_tipo_informacion} (${informacion.acronimo})`}
                                                                                                    onChange={(e) => handleCheckbox(e, `${archivo.titulo_tipo_informacion} (${informacion.acronimo})`)}
                                                                                                    checked={Object.hasOwn(checkboxesTiposInformacion, `${archivo.titulo_tipo_informacion} (${informacion.acronimo})`) ? checkboxesTiposInformacion[`${archivo.titulo_tipo_informacion} (${informacion.acronimo})`] : false}
                                                                                                />
                                                                                            </span>
                                                                                            <span className='col-9'><label className="ps-1 font-size-12" htmlFor={`${archivo.titulo_tipo_informacion} (${informacion.acronimo})`}>{informacion.nombre_proyecto}</label></span>
                                                                                            <span className="col-2 text-end font-mono-data fw-medium text-dark"> <label>{informacion.numero_registros ? Number(informacion.numero_registros).toLocaleString('es-CL') : 0}</label></span>
                                                                                        </div>

                                                                                    </div>
                                                                                ))
                                                                            }

                                                                        </div>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            }

                                        </div>

                                        {
                                            descargas.length > 0 &&
                                            (
                                                <section id="tour-pub-tabla-descargas" className="mt-4 mb-5">
                                                    <div className="data-grid-container">
                                                        <div className="rounded-top-4 border-start border-end border-top border-light-subtle bg-marine-700 text-white p-3 d-flex flex-wrap align-items-center gap-4 card-ai-gradient">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <MapPin size={16} className='lucide opacity-75' aria-hidden="true" />
                                                                <span className="small fw-light">{'Tipos de Información: '}
                                                                    <strong className="fw-bold">{'"'}

                                                                        {
                                                                            valueTiposInformacion.map((tipoInformacion, index, array) =>
                                                                            (
                                                                                <span key={index}>
                                                                                    {tipoInformacion.label} { index !== array.length - 1 && (' / ') }
                                                                                </span>
                                                                            ))
                                                                        }

                                                                    {'"'}</strong>
                                                                </span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Calendar size={16} className='lucide opacity-75' aria-hidden="true" />
                                                                <span className="small fw-light">Año: <strong className="fw-bold">{'"'}{valueAnio ? valueAnio.label : ''}{'"'}</strong></span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <FishIcon size={16} className='lucide opacity-75' aria-hidden="true" /><span className="small fw-light">Región:
                                                                    <strong className="fw-bold"><i>{'"'}{valueRegion ? valueRegion.label : ''}{'"'}</i></strong>
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="table-responsive">
                                                            <table id="tablaResultados" className="table data-grid-table mb-0">
                                                                <thead className='bg-light bg-opacity-50'>
                                                                    <tr>
                                                                        <th className="text-center px-4 py-3 text-uppercase fw-black text-muted small tracking-wider font-size-10" >Tipo de información</th>
                                                                        <th className="text-center px-4 py-3 text-uppercase fw-black text-muted small tracking-wider font-size-10" >Proyecto</th>
                                                                        <th className="text-center px-4 py-3 text-uppercase fw-black text-muted small tracking-wider font-size-10" >Año</th>
                                                                        <th className="text-center px-4 py-3 text-uppercase fw-black text-muted small tracking-wider font-size-10" >Región</th>
                                                                        <th className="text-center px-4 py-3 text-uppercase fw-black text-muted small tracking-wider font-size-10" >Nombre de archivo</th>
                                                                        <th className="text-center px-4 py-3 text-uppercase fw-black text-muted small tracking-wider font-size-10" >Registros</th>
                                                                        <th className="text-center px-4 py-3 text-uppercase fw-black text-muted small tracking-wider font-size-10" >Acción</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>

                                                                    {
                                                                        descargas.map((descarga, index) =>
                                                                        (
                                                                            <tr key={index}>
                                                                                <td className='font-size-10 text-center small'><span className={`badge-info-group ${getBadgeClass(descarga.TIPO_INFORMACION)}`}>{ICON_CONFIG_TABLA[descarga.TIPO_INFORMACION]} {descarga.TIPO_INFORMACION}</span></td>
                                                                                <td className='font-size-10 text-center small'>{descarga.NOMBRE_PROYECTO}</td>
                                                                                <td className='font-size-10 text-center small'>{descarga.ANIO}</td>
                                                                                <td className='font-size-10 text-center small'>{`Región ${descarga.NOMBRE_REG}`}</td>
                                                                                <td className='font-size-10 text-center small'>{descarga.NOMBRE_ARCHIVO}</td>
                                                                                <td className='font-size-10 text-center font-mono-data fw-bold text-primary'>{descarga.NUM_REG > 0 && descarga.NUM_REG.toLocaleString('es-CL')}</td>
                                                                                <td className='font-size-10 text-center'>

                                                                                    {
                                                                                        descarga.NOMBRE &&
                                                                                        (
                                                                                            token ?
                                                                                            (
                                                                                                <a href={`${repositorioUrl}/${descarga.REPOSITORIO}/${descarga.NOMBRE}`}
                                                                                                    className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1 rounded-2"
                                                                                                    onClick={() => handleClick(descarga.NOMBRE, url, descarga.TIPO_INFORMACION, descarga.NOMBRE_PROYECTO, valueRegion, anioDisponible)}
                                                                                                    download>
                                                                                                    <Download size={13} /> XLSX
                                                                                                </a>
                                                                                            ) :
                                                                                            (
                                                                                                <a className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1 rounded-2"
                                                                                                    onClick={() => { setShowLoginModal(true); handleOpciones(`${repositorioUrl}/${descarga.REPOSITORIO}/${descarga.NOMBRE}`, descarga.NOMBRE, url, descarga.TIPO_INFORMACION, descarga.NOMBRE_PROYECTO, valueRegion, anioDisponible); }}>
                                                                                                    <Download size={13} /> XLSX
                                                                                                </a>
                                                                                            )
                                                                                        )
                                                                                    }

                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    }

                                                                </tbody>
                                                            </table>

                                                            <div className='p-4 bg-light bg-opacity-50 border-top border-light-subtle d-flex justify-content-end'>

                                                                {
                                                                    token ?
                                                                    (
                                                                        <button type="button"
                                                                            className="btn btn-primary bg-marine-600 border-0 px-4 py-2 rounded-3 fw-bold small shadow-sm d-flex align-items-center gap-2"
                                                                            onClick={() => { handleDownloadZip(descargasConsolidadas, url, tiposInformacion, valueTiposInformacion, valueRegion, anioDisponible); setDescargar(true); }}
                                                                            disabled={descargar}>
                                                                            <Download size={25} strokeWidth={1.5} /> Descargar
                                                                        </button>
                                                                    ) :
                                                                    (
                                                                        <button type="button"
                                                                            className="btn btn-primary bg-marine-600 border-0 px-4 py-2 rounded-3 fw-bold small shadow-sm d-flex align-items-center gap-2"
                                                                            onClick={() => { setShowLoginModal(true); handleOpcionesConsolidadas(descargasConsolidadas, url, tiposInformacion, valueTiposInformacion, valueRegion, anioDisponible); }}>
                                                                            <Download size={25} strokeWidth={1.5} /> Descargar
                                                                        </button>
                                                                    )
                                                                }

                                                            </div>

                                                        </div>
                                                    </div>
                                                </section>
                                            )
                                        }

                                    </div>
                                )
                            }

                        </div>
                    </main>
                </div>

                <Footer />

            </div>
        </>
    );
}

export default AcuiculturaNuevoV1Page;
